import { Spinner } from '@gmg/gmg-react-components';
import { FunctionComponent, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useGetLicenseSystemAccessByUserId } from 'src/graphql/useGetLicenseSystemAccessByUserId';
import { LicenseSystem } from 'src/viewModels/LicenseSystem';
import { AddOrEditUserModalForm } from '../AddOrEditUserModal';
import ColorCardSettingsModal from './ColorCardSettingsModal';
import { DEFAULT_ROLE_SELECTION } from './cpGo';

export interface ColorCardSettingsModalContainerProps {
    onCloseModal: () => void;
    userId?: string;
    availableLicenseSystems: Array<LicenseSystem>;
}

const ColorCardSettingsModalContainer: FunctionComponent<ColorCardSettingsModalContainerProps> = (props) => {

    const { data: licenseSystemAccessData, isLoading } = useGetLicenseSystemAccessByUserId(props.userId, 'ColorCard');
    const { setValue, getValues } = useFormContext<AddOrEditUserModalForm>();

    const appliedColorCardAssignedLicenseSystems = getValues('colorCardAssignedLicenseSystems');
    const hasValidSettings = !!appliedColorCardAssignedLicenseSystems;
    const shouldSkipDefaultValueSetup = hasValidSettings || isLoading;

    useEffect(() => {
        // Here we set the defaultValues the very first time the cpGoSettings modal displayed
        // and after that we use the values from react-hook-form's state via useFormContext
        if (shouldSkipDefaultValueSetup) return;

        const pairedLicenseSystemIds = licenseSystemAccessData?.map((lsData) => lsData.licenseSystemId) || [];
        const hasPairedLicenseSystems = pairedLicenseSystemIds.length > 0;

        const licenseSystemsBelongingToOrg = props.availableLicenseSystems
            .filter((ls) => pairedLicenseSystemIds.includes(ls.id));
        const licenseSystemsNotBelongingToOrg = pairedLicenseSystemIds
            .filter((lsId) => !props.availableLicenseSystems.some(ls => ls.id === lsId));

        const defaultLicenseSystems = hasPairedLicenseSystems
            ? [
                ...licenseSystemsBelongingToOrg
                    .map(({ id, computerName, location }) => ({ value: id, label: `${id}: ${computerName} — ${location}` })),
                ...licenseSystemsNotBelongingToOrg.map((id) => ({ value: id, label: id })),
            ]
            : undefined;
        setValue('colorCardAssignedLicenseSystems', defaultLicenseSystems);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [licenseSystemAccessData]);


    if (isLoading) {
        return <Spinner />;
    }

    const handleCancelButtonClick = () => {
        setValue('colorCardAssignedLicenseSystems', appliedColorCardAssignedLicenseSystems);
        props.onCloseModal();
    };

    return (
        <ColorCardSettingsModal
            onCloseModal={handleCancelButtonClick}
            onApplyButtonClick={props.onCloseModal}
            allAvailableLicenseSystemOptions={
                props.availableLicenseSystems
                    .filter(ls => ls.cpGo.isLicensed)
                    .map(({ id, computerName, location }) => (
                        { value: id, label: `${id}: ${computerName} — ${location}` }),
                    )}
            shownDefaultCpGoRole={DEFAULT_ROLE_SELECTION}
            alreadyPairedLicenseSystems={appliedColorCardAssignedLicenseSystems}
        />
    );
};

export default ColorCardSettingsModalContainer;